// Weekly Mock — full timed paper in one subject, with a live countdown and a
// band estimate at the end. One per (student, subject, ISO week). Built on the
// same run UX as DailyFocus. The countdown is ADVISORY — it does NOT auto-submit
// at zero; the session persists in_progress and the student submits when ready
// (see the countdown effect). See docs/exam_runway_daily_focus_plan.md.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DrillAnswerResponse, DrillQuestion } from '../services/aptitudeDrillAPI';
import {
  MockCompleteResponse,
  MockTodayResponse,
  weeklyMockAPI,
} from '../services/weeklyMockAPI';
import { track } from '../services/api';
import { displayTopic } from '../config/topicDisplayNames';
import QuizCanvas, { ACCENTS } from '../components/ExamRun/QuizCanvas';
import { useTheme } from '../contexts/ThemeContext';

// Theme-aware palette. Light values are byte-for-byte the originals; dark
// values are tinted/brightened equivalents that read on a dark page.
const makeColors = (dark: boolean) => ({
  primary: dark ? '#a78bfa' : '#6d28d9',
  border: dark ? '#262c35' : '#e2e8f0',
  bg: dark ? '#0f1216' : '#f8fafc',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  wrong: dark ? '#ff6b6b' : '#dc2626',
});

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

const WeeklyMock: React.FC = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  const [searchParams] = useSearchParams();
  const urlSubject = searchParams.get('subject') || undefined;

  const [mock, setMock] = useState<MockTodayResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [selected, setSelected] = useState<string | null>(null);
  const [lastAnswer, setLastAnswer] = useState<DrillAnswerResponse | null>(null);
  const [qStartedAt, setQStartedAt] = useState<number>(Date.now());
  const [recap, setRecap] = useState<MockCompleteResponse | null>(null);

  const [currentIdx, setCurrentIdx] = useState(0);
  const [remaining, setRemaining] = useState<number>(0);
  const initialResumeApplied = useRef(false);
  const finishing = useRef(false);

  // -------------- load --------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await weeklyMockAPI.today(urlSubject);
        if (cancelled) return;
        setMock(data);
        setRemaining(data.remaining_seconds);
        setQStartedAt(Date.now());
        track('weekly_mock.opened', {
          subject: data.subject,
          status: data.status,
          total: data.total_questions,
        });
        if (data.status === 'completed') {
          try {
            const c = await weeklyMockAPI.complete(data.subject);
            if (!cancelled) setRecap(c);
          } catch {
            /* non-critical */
          }
        }
      } catch (e: any) {
        if (e?.response?.status === 401) {
          navigate('/login');
          return;
        }
        setLoadError(e?.response?.data?.detail || "Couldn't load this week's mock.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, urlSubject]);

  const finishMock = useCallback(async () => {
    if (!mock || recap || finishing.current) return;
    finishing.current = true;
    try {
      const c = await weeklyMockAPI.complete(mock.subject);
      setRecap(c);
      track('weekly_mock.completed', {
        subject: c.subject,
        score: c.score,
        total: c.total,
        band: c.band?.band,
        time_spent_seconds: c.time_spent_seconds,
      });
    } catch {
      finishing.current = false;
    }
  }, [mock, recap]);

  // -------------- countdown --------------
  useEffect(() => {
    if (!mock || recap || mock.status === 'completed') return;
    const id = window.setInterval(() => {
      setRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [mock, recap]);

  // NB: we deliberately do NOT auto-submit when the clock hits 0. The timer is
  // advisory (exam-pace awareness); for practice the student finishes when
  // ready or leaves and resumes later (the session persists in_progress).

  useEffect(() => {
    if (!mock || initialResumeApplied.current) return;
    const answered = new Set(mock.progress.answered_ids);
    const idx = mock.questions.findIndex((q) => !answered.has(q.question_id));
    setCurrentIdx(idx === -1 ? mock.questions.length : idx);
    initialResumeApplied.current = true;
  }, [mock]);

  const currentQ: DrillQuestion | null =
    mock && currentIdx < mock.questions.length ? mock.questions[currentIdx] : null;
  const allDone = !!mock && currentIdx >= mock.total_questions;

  const handleSelect = useCallback(
    async (letter: string) => {
      if (!currentQ || submitting || lastAnswer) return;
      setSelected(letter);
      setSubmitting(true);
      try {
        const elapsed = Math.max(0, Math.round((Date.now() - qStartedAt) / 1000));
        const resp = await weeklyMockAPI.answer(
          currentQ.question_id,
          letter,
          elapsed,
          mock?.subject,
        );
        setLastAnswer(resp);
        setMock((prev) => (prev ? { ...prev, progress: resp.progress } : prev));
      } catch (e: any) {
        setSelected(null);
        setLoadError(e?.response?.data?.detail || 'Could not submit answer');
      } finally {
        setSubmitting(false);
      }
    },
    [currentQ, submitting, lastAnswer, qStartedAt, mock],
  );

  const handleNext = useCallback(() => {
    setSelected(null);
    setLastAnswer(null);
    setQStartedAt(Date.now());
    setLoadError(null);
    setCurrentIdx((idx) => idx + 1);
  }, []);

  useEffect(() => {
    if (allDone && !recap) finishMock();
  }, [allDone, recap, finishMock]);

  // -------------- views --------------
  if (loadError && !mock) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={cardStyle(dark)}>
          <div style={{ color: COLORS.wrong, fontWeight: 600 }}>Something went wrong</div>
          <div style={{ marginTop: 6, color: COLORS.textMuted }}>{loadError}</div>
        </div>
      </Shell>
    );
  }
  if (!mock) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Loading this week's mock…</div>
      </Shell>
    );
  }
  if (recap) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <Recap
          recap={recap}
          onContinue={() => navigate('/dashboard')}
          onViewResults={() => navigate('/practice-history')}
        />
      </Shell>
    );
  }
  if (!currentQ) {
    return (
      <Shell onBack={() => navigate('/dashboard')}>
        <div style={{ ...cardStyle(dark), color: COLORS.textMuted }}>Submitting your mock…</div>
      </Shell>
    );
  }

  const qNumber = currentIdx + 1;
  const total = mock.total_questions;
  const lowTime = remaining <= 300;
  const topicDisp = currentQ.topic ? displayTopic(currentQ.topic) || currentQ.topic : '';
  const contextLabel = [mock.subject, topicDisp, currentQ.year_ec || '']
    .filter(Boolean)
    .join(' · ');

  return (
    <QuizCanvas
      accent={ACCENTS.mock}
      onBack={() => navigate('/dashboard')}
      answered={mock.progress.answered}
      qNumber={qNumber}
      total={total}
      rightSlot={
        <span
          data-testid="weekly-mock-timer"
          style={{ fontVariantNumeric: 'tabular-nums', color: lowTime ? '#dc2626' : '#0f172a' }}
        >
          ⏱ {fmt(remaining)}
        </span>
      }
      contextLabel={contextLabel}
      questionText={currentQ.question_text}
      media={currentQ.media}
      options={currentQ.options}
      selected={selected}
      lastAnswer={lastAnswer}
      submitting={submitting}
      onSelect={handleSelect}
      onNext={handleNext}
      isLast={qNumber === total}
      finishLabel="Finish mock"
      questionId={currentQ.question_id}
      translateSurface="weekly_mock_explanation"
      optionTestId={(l) => `mock-option-${l}`}
      feedbackTestId="weekly-mock-feedback"
      runTestId="weekly-mock-run"
    />
  );
};

// ----------- sub-components -----------

const Shell: React.FC<{
  children: React.ReactNode;
  onBack: () => void;
  timer?: React.ReactNode;
  meta?: React.ReactNode;
}> = ({ children, onBack, timer, meta }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  return (
    <div style={{ minHeight: '100vh', background: COLORS.bg }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 14px',
          background: dark ? '#171b21' : 'white',
          borderBottom: `1px solid ${COLORS.border}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={onBack}
          aria-label="Back"
          style={{ background: 'transparent', border: 'none', padding: 6, cursor: 'pointer', color: dark ? '#aab1ba' : '#475569', fontSize: 18 }}
        >
          ←
        </button>
        {timer || <h1 style={{ margin: 0, fontSize: '1rem', color: COLORS.textPrimary }}>Weekly Mock</h1>}
        <div style={{ flex: 1 }} />
        {meta}
      </header>
      <main style={{ maxWidth: 720, margin: '0 auto', padding: '12px 16px 80px' }}>{children}</main>
    </div>
  );
};

const Recap: React.FC<{
  recap: MockCompleteResponse;
  onContinue: () => void;
  onViewResults: () => void;
}> = ({ recap, onContinue, onViewResults }) => {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  const pct = recap.band?.score_pct ?? (recap.total > 0 ? Math.round((recap.score / recap.total) * 100) : 0);
  return (
    <div style={cardStyle(dark)} data-testid="weekly-mock-recap">
      <div style={{ fontSize: 13, color: COLORS.textMuted }}>Weekly Mock · {recap.subject}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: COLORS.textPrimary, marginTop: 4 }}>
        {recap.score} / {recap.total} <span style={{ color: COLORS.textMuted, fontSize: 18 }}>({pct}%)</span>
      </div>

      <div
        data-testid="weekly-mock-band"
        style={{
          marginTop: 12,
          padding: 12,
          background: dark ? 'rgba(139, 92, 246, 0.15)' : '#f5f3ff',
          border: `1px solid ${COLORS.primary}`,
          borderRadius: 10,
          color: dark ? '#c4b5fd' : '#4c1d95',
          fontWeight: 700,
        }}
      >
        {recap.band?.label}
      </div>

      <div style={{ marginTop: 12, color: COLORS.textMuted, fontSize: 14 }}>
        Time: {fmt(recap.time_spent_seconds)} of {recap.time_limit_min} min
      </div>

      {Object.keys(recap.by_topic).length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.textMuted, marginBottom: 6 }}>
            By topic
          </div>
          {Object.entries(recap.by_topic).map(([topic, b]) => (
            <div
              key={topic}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: `1px solid ${COLORS.border}`,
                fontSize: 14,
              }}
            >
              <span>{displayTopic(topic) || topic}</span>
              <span style={{ color: COLORS.textMuted }}>
                {b.correct}/{b.answered}
              </span>
            </div>
          ))}
        </div>
      )}

      <button onClick={onContinue} style={primaryBtnStyle(dark)}>
        Back to Dashboard
      </button>
      <button
        data-testid="practice-history-link"
        onClick={onViewResults}
        style={{
          marginTop: 10,
          width: '100%',
          padding: '8px 14px',
          background: 'transparent',
          color: COLORS.textMuted,
          border: 'none',
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        📚 My past results &amp; mocks ›
      </button>
      <div style={{ marginTop: 12, color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>
        New mock next week.
      </div>
    </div>
  );
};

// ----------- styles -----------
const cardStyle = (dark: boolean): React.CSSProperties => ({
  background: dark ? '#171b21' : 'white',
  border: `1px solid ${makeColors(dark).border}`,
  borderRadius: 14,
  padding: 16,
});
const primaryBtnStyle = (dark: boolean): React.CSSProperties => ({
  marginTop: 12,
  width: '100%',
  padding: '12px 16px',
  background: makeColors(dark).primary,
  color: dark ? '#1e1b2e' : 'white',
  border: 'none',
  borderRadius: 10,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
});

export default WeeklyMock;
